import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Button, useMediaQuery } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { buildClassName } from 'utils/build-class-name';
import { formatNumberWithSuffix } from 'utils/format-number-with-suffix';
import ToggleButtonGroup from 'components/atomic/molecules/toggle-button-group';
import MultiSelectButtonGroup from 'components/atomic/molecules/multi-select-button-group';
import ToggleSwitch from 'components/atomic/molecules/toggle-switch';
import SelectDropdown from 'components/atomic/molecules/select-dropdown';
import IconButton from 'components/atomic/molecules/icon-button';
import ControlButton, { SECONDARY_THEME } from 'components/control/button';
import CloseIcon from 'components/icon/close-icon';
import FilterIcon from 'components/icon/filter-icon';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { createFilterConfigs } from 'components/listing-filter-bar/filter-chip-configs';
import {
  HouseIcon,
  TownhouseIcon,
  LandIcon,
  CommercialIcon,
  FarmIcon,
  CondoIcon,
  GarageIcon,
  PoolIcon,
  WaterfrontIcon,
  FireplaceIcon,
  OpenHouseIcon,
  ComingSoonIcon,
  ImagelessIcon,
} from 'components/atomic/atoms/icons';
import {
  AVAILABLE_STATUS,
  NOT_AVAILABLE_SOLD_STATUS,
  NOT_AVAILABLE_OTHER_STATUS,
  PENDING_STATUS,
  HOUSE_PROPERTY_TYPE,
  TOWNHOUSE_PROPERTY_TYPE,
  CONDO_PROPERTY_TYPE,
  LAND_PROPERTY_TYPE,
  COMMERCIAL_PROPERTY_TYPE,
  FARM_PROPERTY_TYPE,
  DEFAULT_HAS_IMAGE_VALUE,
  DEFAULT_SHOW_COMING_SOON_VALUE,
  ANY_LOCKER_VALUE,
  YES_LOCKER_VALUE,
  NO_LOCKER_VALUE,
} from 'contexts/preferences/listing-params';
import { LOCALE_SEARCH_PATHS, Namespaces, SiteLanguage } from 'types/locales';
import styles from './style.module.scss';

import {
  BEDS_FILTER_OPTION,
  BUY_FILTER_OPTION,
  HOUSE_FILTER_OPTION,
  PRICE_FILTER_OPTION,
  RENT_FILTER_OPTION,
  SOLD_FILTER_OPTION,
} from 'constants/area-map-page-filters';
import type { Filter, Locker, PropertyType, PropertyTypeFilter, Status } from 'contexts/preferences/listing-params/types';
import type { FilterItemData } from 'components/listing-filter-bar/listing_filter_bar';
import type { PartialDeep } from 'type-fest';

//#region Types
export type SearchType = 'buy' | 'rent' | 'sold';
/** Sold is intentionally absent: it is selected through Search Type, which writes the same `status` filter. */
export type ListingStatus = 'active' | 'expired' | 'pending';

/** Subset of the domain Filter the panel reads. `showComingSoon` is optional because the shared filter-bar type omits it. */
export type MapFiltersPanelFilters = Pick<
  Filter,
  | 'rental'
  | 'status'
  | 'homeType'
  | 'priceMin'
  | 'priceMax'
  | 'bedrooms'
  | 'bathrooms'
  | 'sqftMin'
  | 'sqftMax'
  | 'parkingSpaces'
  | 'garage'
  | 'pool'
  | 'waterfront'
  | 'fireplace'
  | 'openHouse'
  | 'hasImage'
  | 'listedSince'
  | 'additional'
> & { showComingSoon?: boolean };

export interface MapFiltersPanelProps {
  /** Current filter state, reflected into the controls */
  filters: MapFiltersPanelFilters;
  /** Applies a partial filter change immediately (mirrors the existing filter modal) */
  onValueChange: (filters: PartialDeep<Filter>, changedFilter?: string) => void;
  /** Additional CSS class applied to the panel root */
  className?: string;
  /**
   * Visual style of the trigger button. `'floating'` drops the border and uses a
   * softer shadow, for placement over a map/photo instead of on a plain toolbar.
   * @default 'default'
   */
  variant?: 'default' | 'floating';
  /** Invoked when "Show Results" is clicked */
  onApply?: () => void;
  /** Invoked after resetting when "Clear" is clicked */
  onClear?: () => void;
  /** Number of listings matching the current filters. When provided, the apply button reads "Show {count} Results". */
  resultsCount?: number;
  /** When true, a filter change is being applied/refetched: shows a blocking translucent overlay over the panel. */
  loading?: boolean;
  /** Called whenever the panel (desktop popover or mobile bottom drawer) opens or closes. */
  onOpenChange?: (isOpen: boolean) => void;
}
//#endregion

//#region Constants
const PRICE_DEBOUNCE_MS = 500;
/** Matches the SCSS `$breakpoint-tablet`; below this the panel renders as a bottom drawer. */
const MOBILE_BREAKPOINT_PX = 768;
/** Drag distance past which releasing the drawer closes it. */
const DRAG_CLOSE_THRESHOLD_PX = 120;
/** Slide up/down transition duration; must match the transition applied to the drawer. */
const DRAWER_TRANSITION_MS = 300;

const HOME_TYPE_KEYS: PropertyType[] = [
  HOUSE_PROPERTY_TYPE,
  TOWNHOUSE_PROPERTY_TYPE,
  CONDO_PROPERTY_TYPE,
  LAND_PROPERTY_TYPE,
  COMMERCIAL_PROPERTY_TYPE,
  FARM_PROPERTY_TYPE,
];

const AMENITY_KEYS = ['garage', 'pool', 'waterfront', 'fireplace', 'openHouse'] as const;
type AmenityKey = typeof AMENITY_KEYS[number];

/** Day values (excluding "any") available in the Listed Within dropdown. */
const LISTED_WITHIN_DAY_VALUES = ['1', '3', '7', '14', '30', '90'];

type TranslateFn = (key: string, options?: { defaultValue?: string }) => string;
//#endregion

//#region Localized option builders
const getSearchTypeOptions = (t: TranslateFn) => [
  { value: 'buy', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_SEARCH_TYPE_BUY, { defaultValue: 'Buy' }) },
  { value: 'rent', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_SEARCH_TYPE_RENT, { defaultValue: 'Rent' }) },
  { value: 'sold', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_SEARCH_TYPE_SOLD, { defaultValue: 'Sold' }) },
];

const getHomeTypeOptions = (t: TranslateFn) => [
  { value: 'house', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HOME_TYPE_HOUSE, { defaultValue: 'House' }), icon: <HouseIcon /> },
  { value: 'townhouse', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HOME_TYPE_TOWNHOUSE, { defaultValue: 'Townhouse' }), icon: <TownhouseIcon /> },
  { value: 'condo', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HOME_TYPE_CONDO, { defaultValue: 'Condo' }), icon: <CondoIcon /> },
  { value: 'land', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HOME_TYPE_LAND, { defaultValue: 'Land' }), icon: <LandIcon /> },
  { value: 'commercial', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HOME_TYPE_COMMERCIAL, { defaultValue: 'Commercial' }), icon: <CommercialIcon /> },
  { value: 'farm', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HOME_TYPE_FARM, { defaultValue: 'Farm' }), icon: <FarmIcon /> },
];

// Numbers render as-is (universal); only the "Any" option needs translation.
const getCountOptions = (t: TranslateFn, max: number) => {
  const anyOption = { value: 'any', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_COUNT_ANY, { defaultValue: 'Any' }) };
  const numberOptions = Array.from({ length: max }, (_, index) => ({ value: String(index + 1), label: `${index + 1}+` }));
  return [anyOption, ...numberOptions];
};

const getAmenityOptions = (t: TranslateFn) => [
  { value: 'garage', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_AMENITY_GARAGE, { defaultValue: 'Garage' }), icon: <GarageIcon /> },
  { value: 'pool', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_AMENITY_POOL, { defaultValue: 'Pool' }), icon: <PoolIcon /> },
  { value: 'waterfront', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_AMENITY_WATERFRONT, { defaultValue: 'Waterfront' }), icon: <WaterfrontIcon /> },
  { value: 'fireplace', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_AMENITY_FIREPLACE, { defaultValue: 'Fireplace' }), icon: <FireplaceIcon /> },
  { value: 'openHouse', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_AMENITY_OPEN_HOUSE, { defaultValue: 'Open House' }), icon: <OpenHouseIcon /> },
];

const getListedWithinOptions = (t: TranslateFn) => [
  { value: '', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LISTED_WITHIN_ANY, { defaultValue: 'Any Time' }) },
  { value: '1', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LISTED_WITHIN_1_DAY, { defaultValue: '1 day' }) },
  { value: '3', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LISTED_WITHIN_3_DAYS, { defaultValue: '3 days' }) },
  { value: '7', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LISTED_WITHIN_1_WEEK, { defaultValue: '1 week' }) },
  { value: '14', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LISTED_WITHIN_2_WEEKS, { defaultValue: '2 weeks' }) },
  { value: '30', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LISTED_WITHIN_1_MONTH, { defaultValue: '1 month' }) },
  { value: '90', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LISTED_WITHIN_3_MONTHS, { defaultValue: '3 months' }) },
];

const getListingStatusOptions = (t: TranslateFn) => [
  { value: 'active', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_STATUS_ACTIVE, { defaultValue: 'Active' }) },
  { value: 'expired', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_STATUS_EXPIRED, { defaultValue: 'Expired' }) },
  { value: 'pending', label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_STATUS_PENDING, { defaultValue: 'Pending' }) },
];

const getLockerOptions = (t: TranslateFn) => [
  { value: ANY_LOCKER_VALUE, label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LOCKER_ANY, { defaultValue: 'Any' }) },
  { value: YES_LOCKER_VALUE, label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LOCKER_YES, { defaultValue: 'Yes' }) },
  { value: NO_LOCKER_VALUE, label: t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LOCKER_NO, { defaultValue: 'No' }) },
];
//#endregion

//#region Filter <-> control mapping helpers
const searchTypeFromFilter = (rental: boolean, status: Status): SearchType => {
  if (status === NOT_AVAILABLE_SOLD_STATUS) {
    return 'sold';
  }
  return rental ? 'rent' : 'buy';
};

const listingStatusFromFilter = (status: Status): ListingStatus | '' => {
  if (status === AVAILABLE_STATUS) {
    return 'active';
  }
  // Sold is only selectable via Search Type, so leave the Listing Status group unselected.
  if (status === NOT_AVAILABLE_SOLD_STATUS) {
    return '';
  }
  if (status === PENDING_STATUS) {
    return 'pending';
  }
  return 'expired';
};

const statusFromListingStatus = (listingStatus: ListingStatus): Status => {
  switch (listingStatus) {
    case 'active':
      return AVAILABLE_STATUS;
    case 'pending':
      return PENDING_STATUS;
    case 'expired':
    default:
      return NOT_AVAILABLE_OTHER_STATUS;
  }
};

// Panel uses 'any' | '1'..'N'; the filter uses '0+' | '1+'..'N+'.
const countFromFilter = (value: string | undefined, max: number): string => {
  if (!value || value === '0+' || value === '0') {
    return 'any';
  }
  const digit = parseInt(value, 10);
  if (Number.isNaN(digit) || digit <= 0) {
    return 'any';
  }
  return String(Math.min(digit, max));
};

const countToFilter = (value: string): string => (value === 'any' ? '0+' : `${value}+`);

// The panel treats "all types enabled" as the default (show everything) and
// surfaces it as an empty selection, so no home-type button appears pressed.
const selectedHomeTypes = (homeType: PropertyTypeFilter): PropertyType[] => {
  const selected = HOME_TYPE_KEYS.filter(key => homeType[key]);
  return selected.length === HOME_TYPE_KEYS.length ? [] : selected;
};

// An empty selection means "show all home types" (the default filter), so it
// maps back to every type enabled; otherwise only the chosen types are enabled.
const homeTypesToFilter = (values: string[]): PropertyTypeFilter => {
  const effectiveValues = values.length === 0 ? [...HOME_TYPE_KEYS] : values;
  return HOME_TYPE_KEYS.reduce((acc, key) => {
    acc[key] = effectiveValues.includes(key);
    return acc;
  }, {} as PropertyTypeFilter);
};

const selectedAmenities = (filters: MapFiltersPanelFilters): AmenityKey[] =>
  AMENITY_KEYS.filter(key => filters[key]);

const listedWithinFromFilter = (listedSince: string | null | undefined): string => {
  if (!listedSince) {
    return '';
  }
  const days = dayjs().startOf('day').diff(dayjs(listedSince).startOf('day'), 'day');
  return LISTED_WITHIN_DAY_VALUES.includes(String(days)) ? String(days) : '';
};

const listedWithinToFilter = (value: string): string | null =>
  value ? dayjs().subtract(parseInt(value, 10), 'day').startOf('day').toISOString() : null;

const parsePriceInput = (raw: string): number | null => {
  const digits = raw.replace(/[^0-9]/g, '');
  return digits ? Number(digits) : null;
};

const formatNumberInput = (value: number | null | undefined): string =>
  value !== null && value !== undefined ? String(value) : '';

const getActiveFilterChips = (filters: MapFiltersPanelFilters): FilterItemData[] => {
  const chips: FilterItemData[] = [];
  const selectedTypes = HOME_TYPE_KEYS.filter(key => filters.homeType[key]);

  if (filters.priceMin != null || filters.priceMax != null) {
    chips.push({ type: 'priceRange', min: filters.priceMin ?? null, max: filters.priceMax ?? null });
  }
  if (filters.sqftMin != null || filters.sqftMax != null) {
    chips.push({ type: 'sqft', sqftMin: filters.sqftMin ?? null, sqftMax: filters.sqftMax ?? null });
  }
  if (filters.bedrooms && filters.bedrooms !== '0+') {
    chips.push({ type: 'bedrooms', bedrooms: filters.bedrooms });
  }
  if (filters.bathrooms && filters.bathrooms !== '0+') {
    chips.push({ type: 'bathrooms', bathrooms: filters.bathrooms });
  }
  if (selectedTypes.length > 0 && selectedTypes.length !== HOME_TYPE_KEYS.length) {
    chips.push({ type: 'propertyType', homeType: filters.homeType });
  }
  if (
    filters.status === NOT_AVAILABLE_SOLD_STATUS ||
    filters.status === NOT_AVAILABLE_OTHER_STATUS ||
    filters.status === PENDING_STATUS
  ) {
    chips.push({ type: 'status', status: filters.status });
  }
  if (filters.rental === true) {
    chips.push({ type: 'rental', rental: true });
  }
  if (filters.garage === true) {
    chips.push({ type: 'garage', garage: true });
  }
  if (filters.pool === true) {
    chips.push({ type: 'pool', pool: true });
  }
  if (filters.waterfront === true) {
    chips.push({ type: 'waterfront', waterfront: true });
  }
  if (filters.fireplace === true) {
    chips.push({ type: 'fireplace', fireplace: true });
  }
  if (filters.openHouse === true) {
    chips.push({ type: 'openHouse', openHouse: true });
  }
  if (filters.parkingSpaces && filters.parkingSpaces !== '0+') {
    chips.push({ type: 'parkingSpaces', parkingSpaces: filters.parkingSpaces });
  }
  if (filters.hasImage !== true) {
    chips.push({ type: 'hasImage', hasImage: filters.hasImage });
  }
  if (filters.listedSince) {
    chips.push({ type: 'listedSince', listedSince: filters.listedSince });
  }

  return chips;
};

/**
 * Text-input state that resets to `source` whenever the underlying prop changes
 * (e.g. an external filter update or Clear), without syncing via an effect.
 * Uses React's sanctioned "adjust state during render on prop change" pattern.
 */
function useControlledText(source: string): [string, (value: string) => void] {
  const [prevSource, setPrevSource] = useState(source);
  const [draft, setDraft] = useState(source);
  if (source !== prevSource) {
    setPrevSource(source);
    setDraft(source);
  }
  return [draft, setDraft];
}

/**
 * MapFiltersPanel - Absolutely-positioned filter panel for the map page.
 *
 * Domain component (Layer 2) composed from atomic molecules. It is a controlled
 * component: all control values are derived from the `filters` prop and every
 * change is emitted immediately through `onValueChange` (mirroring the existing
 * filter modal), except the price/square-footage text inputs which are debounced.
 *
 */
export default function MapFiltersPanel({ filters, onValueChange, className, variant = 'default', onApply, onClear, resultsCount, loading = false, onOpenChange }: MapFiltersPanelProps) {
  const { t, i18n } = useTranslation(Namespaces.SEARCH);

  const filterChipConfigs = createFilterConfigs(t, i18n.language as SiteLanguage);
  const activeFilterChips = getActiveFilterChips(filters);
  const activeFilterCount = activeFilterChips.length;

  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT_PX}px)`);
  const [isOpen, setIsOpen] = useState(false);
  // Mobile bottom-drawer state: `entered` drives the slide-up, `dragY`/`isDragging` drive swipe-to-close.
  const [entered, setEntered] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const touchStartYRef = useRef<number | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // True once a downward drag on the sheet (started at the top of the scroll area) is in progress.
  const draggingSheetRef = useRef(false);

  const openDrawer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    // Start off-screen so the mount effect can animate the slide-up.
    setEntered(false);
    setDragY(0);
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDragging(false);
    setDragY(0);
    if (isMobile) {
      // Let the slide-down transition play before unmounting.
      setEntered(false);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      closeTimerRef.current = setTimeout(() => setIsOpen(false), DRAWER_TRANSITION_MS);
    } else {
      setIsOpen(false);
    }
  }, [isMobile]);

  const handleOutsideClick = () => {
    if (typeof document !== 'undefined' && document.querySelector('.MuiPopover-root, .MuiModal-root, .MuiPopper-root')) {
      return;
    }
    closeDrawer();
  };
  const containerRef = useOutsideClickHandler<HTMLDivElement>(handleOutsideClick);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  // Trigger the slide-up once the drawer is mounted on mobile.
  useEffect(() => {
    if (!isOpen || !isMobile) {
      return;
    }
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [isOpen, isMobile]);

  useEffect(() => () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
  }, []);

  const handleDragStart = (event: React.TouchEvent) => {
    touchStartYRef.current = event.touches[0].clientY;
    draggingSheetRef.current = false;
  };

  const handleDragMove = (event: React.TouchEvent) => {
    if (touchStartYRef.current === null) {
      return;
    }
    const currentY = event.touches[0].clientY;
    // Only take over as a drawer drag when the content is scrolled to the top and the
    // user is pulling down; otherwise let the content scroll normally.
    if (!draggingSheetRef.current) {
      const atTop = (scrollRef.current?.scrollTop ?? 0) <= 0;
      const isPullingDown = currentY - touchStartYRef.current > 0;
      if (atTop && isPullingDown) {
        draggingSheetRef.current = true;
        touchStartYRef.current = currentY; // rebase so the drag starts from 0
        setIsDragging(true);
        setDragY(0);
      }
      return;
    }
    const delta = currentY - touchStartYRef.current;
    setDragY(delta > 0 ? delta : 0);
  };

  const handleDragEnd = () => {
    const wasDraggingSheet = draggingSheetRef.current;
    draggingSheetRef.current = false;
    touchStartYRef.current = null;
    setIsDragging(false);
    if (wasDraggingSheet && dragY > DRAG_CLOSE_THRESHOLD_PX) {
      closeDrawer();
    } else {
      setDragY(0);
    }
  };

  const drawerStyle: React.CSSProperties | undefined = isMobile
    ? {
      transform: entered ? `translateY(${dragY}px)` : 'translateY(100%)',
      transition: isDragging ? 'none' : `transform ${DRAWER_TRANSITION_MS}ms ease`,
    }
    : undefined;

  const searchTypeOptions = getSearchTypeOptions(t);
  const homeTypeOptions = getHomeTypeOptions(t);
  const bedroomOptions = getCountOptions(t, 5);
  const bathroomOptions = getCountOptions(t, 4);
  const parkingOptions = getCountOptions(t, 4);
  const amenityOptions = getAmenityOptions(t);
  const listedWithinOptions = getListedWithinOptions(t);
  const listingStatusOptions = getListingStatusOptions(t);
  const lockerOptions = getLockerOptions(t);

  const searchType = searchTypeFromFilter(filters.rental, filters.status);
  const listingStatus = listingStatusFromFilter(filters.status);
  const homeTypes = selectedHomeTypes(filters.homeType);
  const amenities = selectedAmenities(filters);
  const bedrooms = countFromFilter(filters.bedrooms, 5);
  const bathrooms = countFromFilter(filters.bathrooms, 4);
  const parking = countFromFilter(filters.parkingSpaces, 4);
  const listedWithin = listedWithinFromFilter(filters.listedSince);
  const locker = filters.additional?.condoOrTownhouse?.locker ?? ANY_LOCKER_VALUE;
  const hideNoImages = filters.hasImage ?? DEFAULT_HAS_IMAGE_VALUE;
  const showComingSoon = filters.showComingSoon ?? DEFAULT_SHOW_COMING_SOON_VALUE;

  // Debounced text inputs (price + square footage) keep their own edit state,
  // resetting when the corresponding filter changes externally.
  const [minPriceInput, setMinPriceInput] = useControlledText(formatNumberInput(filters.priceMin));
  const [maxPriceInput, setMaxPriceInput] = useControlledText(formatNumberInput(filters.priceMax));
  const [minSqftInput, setMinSqftInput] = useControlledText(formatNumberInput(filters.sqftMin));
  const [maxSqftInput, setMaxSqftInput] = useControlledText(formatNumberInput(filters.sqftMax));
  const [maxMaintenanceInput, setMaxMaintenanceInput] = useControlledText(formatNumberInput(filters.additional?.condoOrTownhouse?.maintenanceFee));

  const onValueChangeRef = useRef(onValueChange);
  useEffect(() => {
    onValueChangeRef.current = onValueChange;
  }, [onValueChange]);

  // Merge pending numeric-range edits and flush them together after the debounce window.
  const pendingRef = useRef<PartialDeep<Filter>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedCommit = useCallback((partial: PartialDeep<Filter>) => {
    pendingRef.current = { ...pendingRef.current, ...partial };
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      const pending = pendingRef.current;
      pendingRef.current = {};
      const changedFilter = ('priceMin' in pending || 'priceMax' in pending)
        ? PRICE_FILTER_OPTION
        : undefined;
      onValueChangeRef.current(pending, changedFilter);
    }, PRICE_DEBOUNCE_MS);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, []);

  const handleSearchTypeChange = (value: string) => {
    if (value === 'sold') {
      onValueChange({ rental: false, status: NOT_AVAILABLE_SOLD_STATUS }, SOLD_FILTER_OPTION);
    } else {
      onValueChange(
        { rental: value === 'rent', status: AVAILABLE_STATUS },
        value === 'rent' ? RENT_FILTER_OPTION : BUY_FILTER_OPTION
      );
    }
  };

  const handleListingStatusChange = (value: string) => {
    onValueChange({ status: statusFromListingStatus(value as ListingStatus) }, value);
  };

  const handleHomeTypesChange = (values: string[]) => {
    const nextHomeType = homeTypesToFilter(values);
    const previousSelected = selectedHomeTypes(filters.homeType);
    const added = values.filter(value => !previousSelected.includes(value as PropertyType));
    const removed = previousSelected.filter(value => !values.includes(value));
    const toggledPropertyType = added[0] ?? removed[0] ?? HOUSE_FILTER_OPTION;
    onValueChange({ homeType: nextHomeType }, toggledPropertyType);
  };

  const handleAmenitiesChange = (values: string[]) => {
    onValueChange(
      AMENITY_KEYS.reduce((acc, key) => {
        acc[key] = values.includes(key);
        return acc;
      }, {} as Record<AmenityKey, boolean>)
    );
  };

  const handleMinPriceChange = (raw: string) => {
    setMinPriceInput(raw);
    debouncedCommit({ priceMin: parsePriceInput(raw) });
  };

  const handleMaxPriceChange = (raw: string) => {
    setMaxPriceInput(raw);
    debouncedCommit({ priceMax: parsePriceInput(raw) });
  };

  const handleMinSqftChange = (raw: string) => {
    setMinSqftInput(raw);
    debouncedCommit({ sqftMin: parsePriceInput(raw) });
  };

  const handleMaxSqftChange = (raw: string) => {
    setMaxSqftInput(raw);
    debouncedCommit({ sqftMax: parsePriceInput(raw) });
  };

  const handleLockerChange = (value: string) => {
    onValueChange({ additional: { condoOrTownhouse: { locker: value as Locker }}});
  };

  const handleMaxMaintenanceChange = (raw: string) => {
    setMaxMaintenanceInput(raw);
    debouncedCommit({ additional: { condoOrTownhouse: { maintenanceFee: parsePriceInput(raw) }}});
  };

  const handleClear = () => {
    onValueChange({
      rental: false,
      status: AVAILABLE_STATUS,
      homeType: homeTypesToFilter([...HOME_TYPE_KEYS]),
      priceMin: null,
      priceMax: null,
      bedrooms: '0+',
      bathrooms: '0+',
      parkingSpaces: '0+',
      sqftMin: null,
      sqftMax: null,
      garage: false,
      pool: false,
      waterfront: false,
      fireplace: false,
      openHouse: false,
      listedSince: null,
      hasImage: DEFAULT_HAS_IMAGE_VALUE,
      showComingSoon: DEFAULT_SHOW_COMING_SOON_VALUE,
      additional: { condoOrTownhouse: { locker: ANY_LOCKER_VALUE, maintenanceFee: null }},
    });
    onClear?.();
  };

  const handleApply = () => {
    closeDrawer();
    onApply?.();
  };

  return (
    <div className={buildClassName(styles.root, className)} ref={containerRef}>
      <div className={styles.trigger}>
        <ControlButton
          className={buildClassName(
            styles.triggerButton,
            activeFilterCount > 0 && styles.triggerButtonActive,
            variant === 'floating' && styles.triggerButtonFloating
          )}
          Icon={FilterIcon}
          theme={SECONDARY_THEME}
          label={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_BUTTON_FILTERS, { defaultValue: 'Filters' })}
          onClick={() => (isOpen ? closeDrawer() : openDrawer())}
          testId="filter-panel-toggle"
        />
        {activeFilterCount > 0 && (
          <span className={styles.badge} data-testid="filter-panel-badge">{activeFilterCount}</span>
        )}
      </div>
      {isOpen && (
        <>
          {isMobile && (
            <div
              className={styles.backdrop}
              style={{ opacity: entered ? 1 : 0 }}
              onClick={closeDrawer}
              aria-hidden="true"
              data-testid="filter-panel-backdrop"
            />
          )}
          <div
            className={styles.panel}
            style={drawerStyle}
            role="dialog"
            aria-label={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_ARIA_DIALOG, { defaultValue: 'Search filters' })}
            aria-busy={loading}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div
              className={styles.dragHandle}
              aria-hidden="true"
              data-testid="filter-panel-drag-handle"
            />
            <div className={styles.drawerHeader}>
              <h2 className={styles.drawerHeading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_FILTERS, { defaultValue: 'Filters' })}</h2>
              <button
                type="button"
                className={styles.closeButton}
                onClick={closeDrawer}
                aria-label={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_ARIA_CLOSE, { defaultValue: 'Close filters' })}
                data-testid="filter-panel-close"
              >
                <CloseIcon className={styles.closeIcon} />
              </button>
            </div>
            <div className={styles.scroll} ref={scrollRef}>
              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_SEARCH_TYPE, { defaultValue: 'Search Type' })}</h3>
                <ToggleButtonGroup
                  options={searchTypeOptions}
                  value={searchType}
                  onValueChange={handleSearchTypeChange}
                  fullWidth
                  ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_SEARCH_TYPE, { defaultValue: 'Search Type' })}
                  testId="filter-search-type"
                />
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_PRICE_RANGE, { defaultValue: 'Price Range' })}</h3>
                <div className={buildClassName(styles.grid2, styles.priceInputs)}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="filter-min-price">{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LABEL_MIN_PRICE, { defaultValue: 'Min Price' })}</label>
                    <input
                      id="filter-min-price"
                      className={styles.input}
                      inputMode="decimal"
                      placeholder={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_PLACEHOLDER_NO_MIN, { defaultValue: 'No min' })}
                      value={minPriceInput}
                      onChange={event => handleMinPriceChange(event.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="filter-max-price">{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LABEL_MAX_PRICE, { defaultValue: 'Max Price' })}</label>
                    <input
                      id="filter-max-price"
                      className={styles.input}
                      inputMode="decimal"
                      placeholder={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_PLACEHOLDER_NO_MAX, { defaultValue: 'No max' })}
                      value={maxPriceInput}
                      onChange={event => handleMaxPriceChange(event.target.value)}
                    />
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_HOME_TYPE, { defaultValue: 'Home Type' })}</h3>
                <MultiSelectButtonGroup
                  options={homeTypeOptions}
                  values={homeTypes}
                  onValuesChange={handleHomeTypesChange}
                  ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_HOME_TYPE, { defaultValue: 'Home Type' })}
                  testId="filter-home-type"
                />
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_BEDROOMS, { defaultValue: 'Bedrooms' })}</h3>
                <ToggleButtonGroup
                  options={bedroomOptions}
                  value={bedrooms}
                  onValueChange={value => onValueChange({ bedrooms: countToFilter(value) }, BEDS_FILTER_OPTION)}
                  fullWidth
                  ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_BEDROOMS, { defaultValue: 'Bedrooms' })}
                  testId="filter-bedrooms"
                />
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_BATHROOMS, { defaultValue: 'Bathrooms' })}</h3>
                <ToggleButtonGroup
                  options={bathroomOptions}
                  value={bathrooms}
                  onValueChange={value => onValueChange({ bathrooms: countToFilter(value) })}
                  fullWidth
                  ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_BATHROOMS, { defaultValue: 'Bathrooms' })}
                  testId="filter-bathrooms"
                />
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_PARKING, { defaultValue: 'Parking' })}</h3>
                <ToggleButtonGroup
                  options={parkingOptions}
                  value={parking}
                  onValueChange={value => onValueChange({ parkingSpaces: countToFilter(value) })}
                  fullWidth
                  ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_PARKING, { defaultValue: 'Parking' })}
                  testId="filter-parking"
                />
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_SQUARE_FOOTAGE, { defaultValue: 'Square Footage' })}</h3>
                <div className={styles.grid2}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="filter-min-sqft">{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LABEL_MIN_SQFT, { defaultValue: 'Min sqft' })}</label>
                    <input
                      id="filter-min-sqft"
                      className={styles.input}
                      inputMode="numeric"
                      placeholder={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_PLACEHOLDER_NO_MIN, { defaultValue: 'No min' })}
                      value={minSqftInput}
                      onChange={event => handleMinSqftChange(event.target.value)}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="filter-max-sqft">{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_LABEL_MAX_SQFT, { defaultValue: 'Max sqft' })}</label>
                    <input
                      id="filter-max-sqft"
                      className={styles.input}
                      inputMode="numeric"
                      placeholder={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_PLACEHOLDER_NO_MAX, { defaultValue: 'No max' })}
                      value={maxSqftInput}
                      onChange={event => handleMaxSqftChange(event.target.value)}
                    />
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_AMENITIES, { defaultValue: 'Amenities' })}</h3>
                <MultiSelectButtonGroup
                  options={amenityOptions}
                  values={amenities}
                  onValuesChange={handleAmenitiesChange}
                  ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_AMENITIES, { defaultValue: 'Amenities' })}
                  testId="filter-amenities"
                />
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_LISTED_WITHIN, { defaultValue: 'Listed Within' })}</h3>
                <SelectDropdown
                  className={styles.listedWithinDropdown}
                  options={listedWithinOptions}
                  variant="pill"
                  value={listedWithin}
                  onValueChange={value => onValueChange({ listedSince: listedWithinToFilter(value) })}
                  displayEmpty
                  ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_LISTED_WITHIN, { defaultValue: 'Listed Within' })}
                  testId="filter-listed-within"
                  menuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: '24px',
                        overflow: 'hidden',
                        boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.08)',
                        '& .MuiMenuItem-root': {
                          '&:hover': { backgroundColor: 'var(--themes-colors-primary-light)' },
                          '&.Mui-selected': { backgroundColor: 'var(--themes-colors-primary-light)' },
                          '&.Mui-selected:hover': { backgroundColor: 'var(--themes-colors-primary-light)' },
                          '&.Mui-focusVisible': { backgroundColor: 'var(--themes-colors-primary-light)' },
                        },
                      },
                    },
                  }}
                />
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_CONDO_TOWNHOUSE, { defaultValue: 'Condo & Townhouse' })}</h3>
                <div className={styles.condoTownhouse}>
                  <div>
                    <span className={styles.subheading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_LOCKER, { defaultValue: 'Locker' })}</span>
                    <ToggleButtonGroup
                      options={lockerOptions}
                      value={locker}
                      onValueChange={handleLockerChange}
                      fullWidth
                      ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_LOCKER, { defaultValue: 'Locker' })}
                      testId="filter-locker"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.subheading} htmlFor="filter-max-maintenance">{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_MAX_MAINTENANCE, { defaultValue: 'Max Maintenance Fee' })}</label>
                    <input
                      id="filter-max-maintenance"
                      className={styles.input}
                      inputMode="numeric"
                      placeholder={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_PLACEHOLDER_NO_MAX, { defaultValue: 'No max' })}
                      value={maxMaintenanceInput}
                      onChange={event => handleMaxMaintenanceChange(event.target.value)}
                    />
                  </div>
                </div>
              </section>

              <section className={styles.section}>
                <h3 className={styles.heading}>{t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_LISTING_STATUS, { defaultValue: 'Listing Status' })}</h3>
                <div className={styles.statusSection}>
                  <ToggleButtonGroup
                    options={listingStatusOptions}
                    value={listingStatus}
                    onValueChange={handleListingStatusChange}
                    fullWidth
                    ariaLabel={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_HEADING_LISTING_STATUS, { defaultValue: 'Listing Status' })}
                    testId="filter-listing-status"
                  />
                  <div className={styles.switchRow}>
                    <ToggleSwitch
                      className={styles.switch}
                      checked={hideNoImages}
                      onChange={checked => onValueChange({ hasImage: checked })}
                      labelPlacement="start"
                      testId="filter-hide-no-images"
                      label={
                        <span className={styles.switchLabel}>
                          <span className={styles.switchIcon}><ImagelessIcon /></span>
                          {t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_TOGGLE_HIDE_NO_IMAGES, { defaultValue: 'Hide listings without images' })}
                        </span>
                      }
                    />
                    <ToggleSwitch
                      className={styles.switch}
                      checked={showComingSoon}
                      onChange={checked => onValueChange({ showComingSoon: checked })}
                      labelPlacement="start"
                      testId="filter-show-coming-soon"
                      label={
                        <span className={styles.switchLabel}>
                          <span className={styles.switchIcon}><ComingSoonIcon /></span>
                          {t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_TOGGLE_SHOW_COMING_SOON, { defaultValue: 'Show coming soon listings' })}
                        </span>
                      }
                    />
                  </div>
                </div>
              </section>
            </div>

            <div className={styles.footer}>
              {activeFilterChips.length > 0 && (
                <div className={styles.activeFilters} data-testid="filter-active-chips">
                  {activeFilterChips.map((filter, index) => {
                    const config = filterChipConfigs[filter.type];
                    if (!config) {
                      return null;
                    }
                    return (
                      <div key={`${filter.type}-${index}`} className={styles.chip}>
                        <span className={styles.chipLabel}>{config.render(filter)}</span>
                        <CloseIcon
                          className={styles.chipRemove}
                          onClick={() => config.removeAction(onValueChange)}
                          testId={`filter-chip-remove-${filter.type}`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className={styles.footerRow}>
                <IconButton
                  className={styles.clearButton}
                  variant="secondary"
                  label={t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_BUTTON_CLEAR, { defaultValue: 'Clear' })}
                  onClick={handleClear}
                  testId="filter-clear"
                />
                <Button
                  className={styles.showButton}
                  variant="contained"
                  disableElevation
                  onClick={handleApply}
                  data-testid="filter-show-results"
                  sx={{
                    minHeight: '40px',
                    padding: '10px 14px',
                    borderRadius: '100px',
                    fontFamily: 'Manrope, sans-serif',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                    boxShadow: 'none',
                    backgroundColor: 'black',
                    color: 'white',
                    border: '1px solid transparent',
                    '&:hover': {
                      backgroundColor: '#5A5A5A',
                      color: 'white',
                      border: '1px solid transparent',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {resultsCount !== undefined
                    ? t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_BUTTON_SHOW_COUNT_RESULTS, {
                      count: formatNumberWithSuffix(resultsCount, false),
                      defaultValue: `Show ${formatNumberWithSuffix(resultsCount, false)} Results`,
                    })
                    : t(LOCALE_SEARCH_PATHS.FILTER_MAP_PANEL_BUTTON_SHOW_RESULTS, { defaultValue: 'Show Results' })}
                </Button>
              </div>
            </div>
            {loading && (
              <div className={styles.loadingOverlay} data-testid="filter-panel-loading" />
            )}
          </div>
        </>
      )}
    </div>
  );
}
